// Cinematic shots and timelines over the live game (decision 0043: "keyframed shots and short timelines over the live
// game"; 0054: cutscenes as the shot system plus timelines of camera, lines and motion). Spike 002 phase D1, gate D1.4.
//
// A timeline is data: a list of steps run in order from a cursor, the shape a scripted scene is written in. A step
// either takes time (a line, a wait, an actor walk marked `wait`) and moves the cursor, or starts at the cursor and
// runs alongside what follows (a shot, an actor's move, a pose, a state change). `compileTimeline` turns the steps into
// absolute tracks, the shape Unreal's Sequencer and Unity's Timeline hold: camera shots (each a cut, or a timed blend
// from the shot before; a shot may dolly and zoom across its length), actor moves (from where the actor stood, or its
// last mark, to a mark over seconds), and cues at instants (lines, poses, state sets, game events).
//
// Playing is plain data (`SceneState`: the scene, its time, the cues fired so far, the actors' starting places), so a
// game keeps it in its sim and save, steps it on the fixed step and draws its camera at any time in between. Every
// sample is a pure function of the timeline, the time and the game's resolver: the same seed gives the same frames.
// Skipping jumps to the end and returns what the rest of the scene would have done to the world (the actors' last
// marks, the state sets and the events not yet fired); lines, poses and shots are dropped. A game that applies the
// same end on a natural finish and on a skip leaves the world the same either way.
//
// 0.2.0 (capability round 1, IMP-0044, gap G08) adds the beats a scene player needs beyond shots and lines: a fade and
// a letterbox (view tracks sampled like the camera: `fadeAt`, `letterboxAt`), an actor turning to face another actor,
// a mark or a yaw (state: kept on a skip, through `endMarks`), events with arguments (a give, a clock move: the game's
// event names), and a hold: the scene's time stops at it until the game releases it (`releaseHold`), so a
// conversation with choices can play inside a scene, however long the player takes.
import type { Vec3 } from "./math";

/** Planted defects beside what they break: a game's cutscene checks must go red with them on. */
export const TIMELINE_FAULTS = {
  /** Skipping drops the rest of the scene's state changes (actors stay where they were, sets and events are lost):
   *  a skip-against-watch check must fail. */
  skipDropsState: false,
  /** The scene's clock reads the wall clock instead of the dt it is stepped by: a same-frames-twice check must fail. */
  wallClock: false,
  /** A hold doesn't stop the scene's time: a scene runs on past a conversation that hasn't ended. */
  holdIgnored: false,
  /** A fade or a letterbox jumps to its end at once instead of easing over its seconds. */
  fadeSnaps: false,
  /** A face step is ignored: the actor keeps the way it faced. */
  faceIgnored: false,
  /** A face step toward an actor reads the target where it stands at the time sampled, not at the step's time: the
   *  facing actor tracks a walker instead of turning once (as in 0.2.0). */
  faceTracksTarget: false,
  /** A face step's look-back isn't kept within a call: the cost doubles with every face step (the cost check must go
   *  red). */
  faceLookBackUncached: false,
};

/** A point in a timeline: world metres, or relative to an actor (in its facing frame when `local`) or a mark. */
export type Anchor = [number, number, number] | { actor: string; offset?: [number, number, number]; local?: boolean } | { mark: string; offset?: [number, number, number]; local?: boolean };

/** How a blend or a move eases. */
export type Ease = "linear" | "in" | "out" | "inOut";

/** A camera shot: where the eye is, what it looks at, the lens; a moving shot travels to `eyeTo`, `lookTo` and
 *  `fovTo` over its length. */
export interface ShotSpec {
  eye: Anchor;
  look: Anchor;
  /** Vertical field of view, degrees. */
  fov: number;
  eyeTo?: Anchor;
  lookTo?: Anchor;
  fovTo?: number;
  /** The move's easing (default "inOut"). */
  ease?: Ease;
}

/** One step of a timeline, as written. */
export type TimelineStep =
  /** Cut (`blend` 0, the default) or blend to a shot; it holds until the next shot or the scene's end. */
  | { kind: "shot"; shot: ShotSpec; blend?: number; ease?: Ease }
  /** Put an actor on a mark: at once (`seconds` 0), or walking there over `seconds`; `wait` holds the cursor till it
   *  arrives. `yaw` faces it (radians; default the mark's own). */
  | { kind: "mark"; actor: string; mark: string; seconds?: number; yaw?: number; wait?: boolean }
  /** Play a pose on an actor (the game's pose library). */
  | { kind: "pose"; actor: string; pose: string }
  /** A line: `speaker` says the string `line` for `seconds` (the cursor waits unless `async`). */
  | { kind: "line"; speaker: string; line: string; seconds: number; async?: boolean }
  /** Wait. */
  | { kind: "wait"; seconds: number }
  /** Set a piece of state the game owns (a story flag): kept when the scene is skipped. */
  | { kind: "set"; key: string; value: string | number | boolean }
  /** Emit a game event by name, with arguments (the game maps it: a give, a clock move): kept when the scene is
   *  skipped. */
  | { kind: "event"; name: string; args?: Record<string, string | number | boolean> }
  /** Fade to `to` (0 clear, 1 black) over `seconds` (default 0: a cut); `wait` holds the cursor till it ends. */
  | { kind: "fade"; to: number; seconds?: number; wait?: boolean }
  /** Letterbox bars in (`on`) or out over `seconds` (default 0). */
  | { kind: "letterbox"; on: boolean; seconds?: number }
  /** Turn an actor at once to face another actor (where it stands at the step's time: a walker isn't tracked), a mark,
   *  or a yaw (radians). Kept when the scene is skipped. */
  | { kind: "face"; actor: string; toward: { actor: string } | { mark: string } | { yaw: number } }
  /** Stop the scene's time here until the game releases `id` (a conversation inside the scene). */
  | { kind: "hold"; id: string };

/** A timeline as content. */
export interface TimelineJson {
  id: string;
  steps: TimelineStep[];
}

/** An instant in a compiled timeline. `line` and `pose` are view cues (dropped on a skip); `set` and `event` are state. */
export type Cue =
  | { at: number; kind: "line"; speaker: string; line: string; seconds: number }
  | { at: number; kind: "pose"; actor: string; pose: string }
  | { at: number; kind: "set"; key: string; value: string | number | boolean }
  | { at: number; kind: "event"; name: string; args?: Record<string, string | number | boolean> }
  | { at: number; kind: "hold"; id: string };

/** A view track's change: the fade or the letterbox moving to `to` from `at` over `seconds`. */
export interface ViewChange {
  at: number;
  kind: "fade" | "letterbox";
  to: number;
  seconds: number;
}

/** A shot on the camera track: from `at` until `end`, blended in from the shot before over `blend` seconds. */
export interface ShotClip {
  at: number;
  end: number;
  shot: ShotSpec;
  blend: number;
  ease: Ease;
}

/** An actor's move: from `from` to `to` seconds, to `mark` (at once when `from` equals `to`). A face step is a move
 *  in place (`mark` empty, `face` set) at one instant. */
export interface ActorMove {
  actor: string;
  mark: string;
  from: number;
  to: number;
  yaw: number | null;
  face?: { actor: string } | { mark: string } | { yaw: number };
}

/** A timeline compiled to absolute tracks. */
export interface CompiledTimeline {
  id: string;
  /** Seconds. */
  duration: number;
  shots: ShotClip[];
  moves: ActorMove[];
  /** Sorted by time (steps' order breaks ties). */
  cues: Cue[];
  /** The fade and letterbox changes, in time order. */
  view: ViewChange[];
  /** Every actor the timeline names. */
  actors: string[];
  /** The times a test samples it at: every step's start, each shot's start and the end. */
  marks: number[];
}

/** Where an actor or a mark stands. */
export interface Placed {
  pos: [number, number, number];
  yaw: number;
}

/** What the game binds: its actors (where each stands now) and its marks (named spots in the world). */
export interface SceneResolver {
  actor(id: string): Placed | null;
  mark(id: string): Placed | null;
}

/** The camera a shot gives at an instant. */
export interface ShotPose {
  position: Vec3;
  target: Vec3;
  fov: number;
}

/** Problems with a timeline (each named by its step): unknown names when the game passes its lists in. */
export function validateTimeline(tl: TimelineJson, names: { actors?: readonly string[]; marks?: readonly string[]; poses?: readonly string[]; lines?: readonly string[] } = {}): string[] {
  const out: string[] = [];
  const has = (list: readonly string[] | undefined, v: string): boolean => !list || list.includes(v);
  const anchor = (a: Anchor | undefined, where: string): void => {
    if (!a || Array.isArray(a)) return;
    if ("actor" in a && !has(names.actors, a.actor)) out.push(`${where}: unknown actor "${a.actor}"`);
    if ("mark" in a && !has(names.marks, a.mark)) out.push(`${where}: unknown mark "${a.mark}"`);
  };
  if (!tl.steps.length) out.push(`${tl.id}: no steps`);
  tl.steps.forEach((s, i) => {
    const w = `${tl.id} step ${i} (${s.kind})`;
    if (s.kind === "shot") {
      anchor(s.shot.eye, w);
      anchor(s.shot.look, w);
      anchor(s.shot.eyeTo, w);
      anchor(s.shot.lookTo, w);
      if (!(s.shot.fov > 1 && s.shot.fov < 170)) out.push(`${w}: fov out of range`);
      if ((s.blend ?? 0) < 0) out.push(`${w}: negative blend`);
    } else if (s.kind === "mark") {
      if (!has(names.actors, s.actor)) out.push(`${w}: unknown actor "${s.actor}"`);
      if (!has(names.marks, s.mark)) out.push(`${w}: unknown mark "${s.mark}"`);
      if ((s.seconds ?? 0) < 0) out.push(`${w}: negative seconds`);
    } else if (s.kind === "pose") {
      if (!has(names.actors, s.actor)) out.push(`${w}: unknown actor "${s.actor}"`);
      if (!has(names.poses, s.pose)) out.push(`${w}: unknown pose "${s.pose}"`);
    } else if (s.kind === "line") {
      if (!has(names.lines, s.line)) out.push(`${w}: unknown line "${s.line}"`);
      if (!(s.seconds > 0)) out.push(`${w}: a line needs seconds`);
    } else if (s.kind === "wait" && !(s.seconds >= 0)) out.push(`${w}: negative wait`);
    else if (s.kind === "fade") {
      if (!(s.to >= 0 && s.to <= 1)) out.push(`${w}: a fade goes to a level in [0, 1]`);
      if ((s.seconds ?? 0) < 0) out.push(`${w}: negative seconds`);
    } else if (s.kind === "letterbox" && (s.seconds ?? 0) < 0) out.push(`${w}: negative seconds`);
    else if (s.kind === "face") {
      if (!has(names.actors, s.actor)) out.push(`${w}: unknown actor "${s.actor}"`);
      if ("actor" in s.toward && !has(names.actors, s.toward.actor)) out.push(`${w}: unknown actor "${s.toward.actor}"`);
      if ("mark" in s.toward && !has(names.marks, s.toward.mark)) out.push(`${w}: unknown mark "${s.toward.mark}"`);
    } else if (s.kind === "hold") {
      if (!s.id) out.push(`${w}: a hold needs an id`);
      if (tl.steps.filter((x) => x.kind === "hold" && x.id === s.id).length > 1) out.push(`${w}: two holds named "${s.id}"`);
    }
  });
  if (!tl.steps.some((s) => s.kind === "shot")) out.push(`${tl.id}: no shot`);
  return out;
}

/** Compile a timeline's steps to absolute tracks. */
export function compileTimeline(tl: TimelineJson): CompiledTimeline {
  let cursor = 0;
  let end = 0;
  const shots: ShotClip[] = [];
  const moves: ActorMove[] = [];
  const cues: Cue[] = [];
  const view: ViewChange[] = [];
  const actors = new Set<string>();
  const marks = new Set<number>([0]);
  const stretch = (t: number): void => void (end = Math.max(end, t));
  for (const s of tl.steps) {
    marks.add(cursor);
    switch (s.kind) {
      case "shot": {
        const prev = shots[shots.length - 1];
        if (prev) prev.end = cursor;
        shots.push({ at: cursor, end: Infinity, shot: s.shot, blend: prev ? Math.max(0, s.blend ?? 0) : 0, ease: s.ease ?? "inOut" });
        for (const a of [s.shot.eye, s.shot.look, s.shot.eyeTo, s.shot.lookTo]) if (a && !Array.isArray(a) && "actor" in a) actors.add(a.actor);
        break;
      }
      case "mark": {
        const secs = Math.max(0, s.seconds ?? 0);
        moves.push({ actor: s.actor, mark: s.mark, from: cursor, to: cursor + secs, yaw: s.yaw ?? null });
        actors.add(s.actor);
        stretch(cursor + secs);
        if (s.wait) cursor += secs;
        break;
      }
      case "pose":
        cues.push({ at: cursor, kind: "pose", actor: s.actor, pose: s.pose });
        actors.add(s.actor);
        break;
      case "line":
        cues.push({ at: cursor, kind: "line", speaker: s.speaker, line: s.line, seconds: s.seconds });
        stretch(cursor + s.seconds);
        if (!s.async) cursor += s.seconds;
        break;
      case "wait":
        cursor += Math.max(0, s.seconds);
        break;
      case "set":
        cues.push({ at: cursor, kind: "set", key: s.key, value: s.value });
        break;
      case "event":
        cues.push(s.args ? { at: cursor, kind: "event", name: s.name, args: { ...s.args } } : { at: cursor, kind: "event", name: s.name });
        break;
      case "fade":
      case "letterbox": {
        const secs = Math.max(0, s.seconds ?? 0);
        view.push({ at: cursor, kind: s.kind, to: s.kind === "fade" ? s.to : s.on ? 1 : 0, seconds: secs });
        stretch(cursor + secs);
        if (s.kind === "fade" && s.wait) cursor += secs;
        break;
      }
      case "face":
        moves.push({ actor: s.actor, mark: "", from: cursor, to: cursor, yaw: null, face: s.toward });
        actors.add(s.actor);
        if ("actor" in s.toward) actors.add(s.toward.actor);
        break;
      case "hold":
        cues.push({ at: cursor, kind: "hold", id: s.id });
        break;
    }
    stretch(cursor);
  }
  const duration = end;
  const last = shots[shots.length - 1];
  if (last) last.end = duration;
  marks.add(duration);
  // the stable sort keeps the steps' order at equal times
  cues.sort((a, b) => a.at - b.at);
  return { id: tl.id, duration, shots, moves, cues, view, actors: [...actors], marks: [...marks].sort((a, b) => a - b) };
}

/** Ease `u` (0 to 1). */
export function easeOf(kind: Ease, u: number): number {
  const x = Math.min(1, Math.max(0, u));
  switch (kind) {
    case "linear":
      return x;
    case "in":
      return x * x;
    case "out":
      return 1 - (1 - x) * (1 - x);
    case "inOut":
      return x * x * (3 - 2 * x);
  }
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpV = (a: Vec3, b: Vec3, t: number): Vec3 => ({ x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), z: lerp(a.z, b.z, t) });

/** Where an anchor is, given where the actors stand at this instant. */
export function anchorAt(a: Anchor, actors: (id: string) => Placed | null, marks: (id: string) => Placed | null): Vec3 {
  if (Array.isArray(a)) return { x: a[0], y: a[1], z: a[2] };
  const base = "actor" in a ? actors(a.actor) : marks(a.mark);
  if (!base) throw new Error(`timeline: no ${"actor" in a ? `actor "${a.actor}"` : `mark "${a.mark}"`}`);
  const o = a.offset ?? [0, 0, 0];
  if (!a.local) return { x: base.pos[0] + o[0], y: base.pos[1] + o[1], z: base.pos[2] + o[2] };
  // the offset in the anchor's facing frame: +z ahead (yaw 0 faces +z), +x to its left
  const c = Math.cos(base.yaw);
  const s = Math.sin(base.yaw);
  return { x: base.pos[0] + o[0] * c + o[2] * s, y: base.pos[1] + o[1], z: base.pos[2] - o[0] * s + o[2] * c };
}

/** A shot's camera at `u` (0 to 1 through it). */
export function shotPose(shot: ShotSpec, u: number, actors: (id: string) => Placed | null, marks: (id: string) => Placed | null): ShotPose {
  const k = easeOf(shot.ease ?? "inOut", u);
  const eye = anchorAt(shot.eye, actors, marks);
  const look = anchorAt(shot.look, actors, marks);
  return {
    position: shot.eyeTo ? lerpV(eye, anchorAt(shot.eyeTo, actors, marks), k) : eye,
    target: shot.lookTo ? lerpV(look, anchorAt(shot.lookTo, actors, marks), k) : look,
    fov: shot.fovTo !== undefined ? lerp(shot.fov, shot.fovTo, k) : shot.fov,
  };
}

/** Blend two camera poses (w 0: `a`, 1: `b`). */
export function blendPoses(a: ShotPose, b: ShotPose, w: number): ShotPose {
  return { position: lerpV(a.position, b.position, w), target: lerpV(a.target, b.target, w), fov: lerp(a.fov, b.fov, w) };
}

const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));

/** Where every actor the timeline moves stands at time `t`: from where it stood at the start (`starts`), along its
 *  moves. Actors with no move are left out (the game keeps placing them). A face toward an actor turns once, toward
 *  where that actor stands at the face step's time (moved by the timeline, else `starts`, else `actors`), and keeps
 *  that facing while the target walks on. */
export function actorsAt(tl: CompiledTimeline, t: number, starts: Record<string, Placed>, marks: (id: string) => Placed | null, actors?: (id: string) => Placed | null): Record<string, Placed> {
  return placeAlong(tl, tl.moves.length, t, starts, marks, actors, new Map());
}

/** `actorsAt` over the first `upto` moves (a face step reads the moves before it at its own time). `memo` keeps each
 *  face step's look-back within one call: the look-back of step i at its own time is the same wherever it is asked
 *  from, so a timeline of n face steps costs O(n²) moves, not O(2ⁿ). */
function placeAlong(tl: CompiledTimeline, upto: number, t: number, starts: Record<string, Placed>, marks: (id: string) => Placed | null, actors: ((id: string) => Placed | null) | undefined, memo: Map<number, Record<string, Placed>>): Record<string, Placed> {
  const out: Record<string, Placed> = {};
  for (let i = 0; i < upto; i++) {
    const mv = tl.moves[i]!;
    if (mv.face) {
      const here = out[mv.actor] ?? starts[mv.actor] ?? actors?.(mv.actor) ?? null;
      if (!here) continue;
      if (mv.from > t || TIMELINE_FAULTS.faceIgnored) {
        out[mv.actor] = { pos: [...here.pos], yaw: here.yaw };
        continue;
      }
      const f = mv.face;
      let yaw = here.yaw;
      if ("yaw" in f) yaw = f.yaw;
      else {
        // both where they stood at the step's time (the moves listed before it), not where they stand at `t`
        let then = out;
        if (mv.from < t && !TIMELINE_FAULTS.faceTracksTarget) {
          let back = TIMELINE_FAULTS.faceLookBackUncached ? undefined : memo.get(i);
          if (!back) {
            back = placeAlong(tl, i, mv.from, starts, marks, actors, memo);
            memo.set(i, back);
          }
          then = back;
        }
        const self = then[mv.actor] ?? here;
        const to = "actor" in f ? (then[f.actor] ?? starts[f.actor] ?? actors?.(f.actor) ?? null) : marks(f.mark);
        if (to) {
          const dx = to.pos[0] - self.pos[0];
          const dz = to.pos[2] - self.pos[2];
          if (Math.hypot(dx, dz) > 1e-6) yaw = Math.atan2(dx, dz);
        }
      }
      out[mv.actor] = { pos: [...here.pos], yaw };
      continue;
    }
    if (mv.from > t && out[mv.actor]) continue;
    const from = out[mv.actor] ?? starts[mv.actor];
    if (mv.from > t) {
      if (from) out[mv.actor] = { pos: [...from.pos], yaw: from.yaw };
      continue;
    }
    const m = marks(mv.mark);
    if (!m) throw new Error(`timeline: no mark "${mv.mark}"`);
    const yaw = mv.yaw ?? m.yaw;
    const u = mv.to > mv.from ? Math.min(1, (t - mv.from) / (mv.to - mv.from)) : 1;
    if (u >= 1) {
      out[mv.actor] = { pos: [...m.pos], yaw };
      continue;
    }
    if (!from) continue;
    // walking: facing the way it goes, turning to the mark's facing as it arrives
    const dx = m.pos[0] - from.pos[0];
    const dz = m.pos[2] - from.pos[2];
    const heading = Math.hypot(dx, dz) > 1e-6 ? Math.atan2(dx, dz) : yaw;
    const turn = Math.max(0, (u - 0.85) / 0.15);
    out[mv.actor] = { pos: [lerp(from.pos[0], m.pos[0], u), lerp(from.pos[1], m.pos[1], u), lerp(from.pos[2], m.pos[2], u)], yaw: wrap(heading + wrap(yaw - heading) * turn) };
  }
  return out;
}

/** The camera at time `t` (null before the first shot and after the end: the game's own camera then). */
export function cameraAt(tl: CompiledTimeline, t: number, starts: Record<string, Placed>, r: SceneResolver): ShotPose | null {
  if (t < 0 || t > tl.duration || !tl.shots.length) return null;
  let i = -1;
  for (let k = 0; k < tl.shots.length; k++) if (tl.shots[k]!.at <= t) i = k;
  if (i < 0) return null;
  const moved = actorsAt(tl, t, starts, (id) => r.mark(id), (id) => r.actor(id));
  const actor = (id: string): Placed | null => moved[id] ?? r.actor(id);
  const mark = (id: string): Placed | null => r.mark(id);
  const poseOf = (c: ShotClip): ShotPose => shotPose(c.shot, c.end > c.at ? (t - c.at) / (c.end - c.at) : 1, actor, mark);
  const clip = tl.shots[i]!;
  const pose = poseOf(clip);
  if (clip.blend > 0 && i > 0 && t < clip.at + clip.blend) return blendPoses(poseOf(tl.shots[i - 1]!), pose, easeOf(clip.ease, (t - clip.at) / clip.blend));
  return pose;
}

/** The lines being spoken at `t` (a line from its cue for its seconds). */
export function linesAt(tl: CompiledTimeline, t: number): Extract<Cue, { kind: "line" }>[] {
  return tl.cues.filter((c): c is Extract<Cue, { kind: "line" }> => c.kind === "line" && c.at <= t && t < c.at + c.seconds);
}

/** A view track's level at `t` (0 before its first change): each change eases linearly from the level at its start to
 *  its `to` over its seconds. */
function viewAt(tl: CompiledTimeline, kind: ViewChange["kind"], t: number): number {
  let level = 0;
  for (const v of tl.view) {
    if (v.kind !== kind || v.at > t) continue;
    if (v.seconds <= 0 || TIMELINE_FAULTS.fadeSnaps || t >= v.at + v.seconds) level = v.to;
    else level = level + (v.to - level) * ((t - v.at) / v.seconds);
  }
  return level;
}

/** The fade at `t`: 0 clear to 1 black. */
export const fadeAt = (tl: CompiledTimeline, t: number): number => viewAt(tl, "fade", t);

/** The letterbox at `t`: 0 none to 1 full bars. */
export const letterboxAt = (tl: CompiledTimeline, t: number): number => viewAt(tl, "letterbox", t);

/** A scene playing: plain data for the game's sim and save. */
export interface SceneState {
  scene: string;
  /** Seconds into the scene. */
  t: number;
  /** Cues fired so far (an index into the compiled cues). */
  fired: number;
  /** Where each actor the scene moves stood when it began. */
  starts: Record<string, Placed>;
  done: boolean;
  skipped: boolean;
  /** The hold the scene waits on (its time stopped), or null. Absent in saves from before 0.2.0: none. */
  held?: string | null;
}

/** Start a scene: its actors' places now. */
export function startScene(tl: CompiledTimeline, r: SceneResolver): SceneState {
  const starts: Record<string, Placed> = {};
  for (const a of tl.actors) {
    const p = r.actor(a);
    if (p) starts[a] = { pos: [...p.pos], yaw: p.yaw };
  }
  return { scene: tl.id, t: 0, fired: 0, starts, done: false, skipped: false, held: null };
}

/** What the world gets at a scene's end: each moved actor on its last mark, facing as its last face step left it.
 *  Pass the scene's state so a skip under the planted defect `skipDropsState` leaves the actors where they stood (and
 *  so an actor that only turned is known from where it stood); `actors` places one that neither moved nor started. */
export function endMarks(tl: CompiledTimeline, marks: (id: string) => Placed | null, st?: SceneState, actors?: (id: string) => Placed | null): Record<string, Placed> {
  if (st?.skipped && TIMELINE_FAULTS.skipDropsState) return {};
  return actorsAt(tl, Infinity, st?.starts ?? {}, marks, actors);
}

/** Release a hold the scene waits on (the conversation it held for ended). Returns whether it was waiting on `id`. */
export function releaseHold(st: SceneState, id: string): boolean {
  if (st.held !== id) return false;
  st.held = null;
  return true;
}

let wallLast: number | null = null;

/** Step a scene by `dt` seconds: the cues it passes, and whether it finished (its end applies the actors' last marks:
 *  `endMarks`). */
export function advanceScene(tl: CompiledTimeline, st: SceneState, dt: number): { fired: Cue[]; finished: boolean } {
  if (st.done) return { fired: [], finished: false };
  let step = dt;
  if (TIMELINE_FAULTS.wallClock) {
    const now = typeof performance !== "undefined" ? performance.now() / 1000 : Date.now() / 1000;
    step = wallLast === null ? dt : Math.max(0, now - wallLast);
    wallLast = now;
  }
  // waiting on a hold: the scene's time stands still until the game releases it
  if (st.held && !TIMELINE_FAULTS.holdIgnored) return { fired: [], finished: false };
  const target = Math.min(tl.duration, st.t + step);
  const fired: Cue[] = [];
  while (st.fired < tl.cues.length && tl.cues[st.fired]!.at <= target + 1e-9) {
    const c = tl.cues[st.fired++]!;
    fired.push(c);
    if (c.kind === "hold" && !TIMELINE_FAULTS.holdIgnored) {
      st.held = c.id;
      st.t = c.at;
      return { fired, finished: false };
    }
  }
  st.t = target;
  if (st.t >= tl.duration - 1e-9) {
    st.done = true;
    return { fired, finished: true };
  }
  return { fired, finished: false };
}

/** Skip to the end: the state cues not yet fired (sets and events; lines and poses dropped). The caller applies them
 *  and the actors' last marks, as on a natural finish. */
export function skipScene(tl: CompiledTimeline, st: SceneState): { fired: Cue[]; finished: boolean } {
  if (st.done) return { fired: [], finished: false };
  const rest = tl.cues.slice(st.fired).filter((c) => c.kind === "set" || c.kind === "event");
  st.fired = tl.cues.length;
  st.t = tl.duration;
  st.done = true;
  st.skipped = true;
  st.held = null;
  return { fired: TIMELINE_FAULTS.skipDropsState ? [] : rest, finished: true };
}

/** The timeline format as a JSON Schema (draft-07) for a game's content check; names are the game's to check. */
export function timelineSchema(extra: { title?: string; description?: string; properties?: Record<string, unknown>; required?: string[] } = {}): Record<string, unknown> {
  const v3 = { type: "array", items: { type: "number", minimum: -10000, maximum: 10000 }, minItems: 3, maxItems: 3 };
  const anchor = {
    oneOf: [
      v3,
      { type: "object", additionalProperties: false, required: ["actor"], properties: { actor: { type: "string", minLength: 1 }, offset: v3, local: { type: "boolean" } } },
      { type: "object", additionalProperties: false, required: ["mark"], properties: { mark: { type: "string", minLength: 1 }, offset: v3, local: { type: "boolean" } } },
    ],
  };
  const ease = { enum: ["linear", "in", "out", "inOut"] };
  const secs = { type: "number", minimum: 0, maximum: 600 };
  const shot = {
    type: "object",
    additionalProperties: false,
    required: ["eye", "look", "fov"],
    properties: { eye: anchor, look: anchor, fov: { type: "number", minimum: 5, maximum: 120 }, eyeTo: anchor, lookTo: anchor, fovTo: { type: "number", minimum: 5, maximum: 120 }, ease },
  };
  const step = (kind: string, props: Record<string, unknown>, required: string[]): Record<string, unknown> => ({
    type: "object",
    additionalProperties: false,
    required: ["kind", ...required],
    properties: { kind: { const: kind }, ...props },
  });
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: extra.title ?? "Timeline",
    description: extra.description ?? "A cutscene timeline as data (@rpg/camera): steps run from a cursor (shots, actor marks, poses, lines, waits, state sets, events, fades, letterbox, faces, holds).",
    type: "object",
    additionalProperties: false,
    required: ["id", "steps", ...(extra.required ?? [])],
    properties: {
      id: { type: "string", minLength: 1 },
      steps: {
        type: "array",
        minItems: 1,
        items: {
          oneOf: [
            step("shot", { shot, blend: secs, ease }, ["shot"]),
            step("mark", { actor: { type: "string", minLength: 1 }, mark: { type: "string", minLength: 1 }, seconds: secs, yaw: { type: "number", minimum: -7, maximum: 7 }, wait: { type: "boolean" } }, ["actor", "mark"]),
            step("pose", { actor: { type: "string", minLength: 1 }, pose: { type: "string", minLength: 1 } }, ["actor", "pose"]),
            step("line", { speaker: { type: "string", minLength: 1 }, line: { type: "string", minLength: 1 }, seconds: { type: "number", minimum: 0.1, maximum: 60 }, async: { type: "boolean" } }, ["speaker", "line", "seconds"]),
            step("wait", { seconds: secs }, ["seconds"]),
            step("set", { key: { type: "string", minLength: 1 }, value: { type: ["string", "number", "boolean"] } }, ["key", "value"]),
            step("event", { name: { type: "string", minLength: 1 }, args: { type: "object", additionalProperties: { type: ["string", "number", "boolean"] } } }, ["name"]),
            step("fade", { to: { type: "number", minimum: 0, maximum: 1 }, seconds: secs, wait: { type: "boolean" } }, ["to"]),
            step("letterbox", { on: { type: "boolean" }, seconds: secs }, ["on"]),
            step(
              "face",
              {
                actor: { type: "string", minLength: 1 },
                toward: {
                  oneOf: [
                    { type: "object", additionalProperties: false, required: ["actor"], properties: { actor: { type: "string", minLength: 1 } } },
                    { type: "object", additionalProperties: false, required: ["mark"], properties: { mark: { type: "string", minLength: 1 } } },
                    { type: "object", additionalProperties: false, required: ["yaw"], properties: { yaw: { type: "number", minimum: -7, maximum: 7 } } },
                  ],
                },
              },
              ["actor", "toward"],
            ),
            step("hold", { id: { type: "string", minLength: 1 } }, ["id"]),
          ],
        },
      },
      ...extra.properties,
    },
  };
}
